import { useEffect, useState } from 'react';
import * as RadixSlider from '@radix-ui/react-slider';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Clock, MapPin, PartyPopper, Users, Loader2, Globe, CloudRain, Crosshair,
} from 'lucide-react';
import type { WizardMode } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import { getCampaignTypeByKey } from '@/lib/campaignTypeRegistry';

interface CorridorRow {
  id: string;
  name: string;
}

// Maps to the `ad_audience` enum on `ad_campaigns`. The enum is single-valued
// (each campaign row has one audience), so "Show on both passenger AND driver"
// is expressed as `all` (which also includes tourists). Operators almost
// always want `all`; we keep the granular roles for the rare case of a
// driver-only or passenger-only campaign.
const AUDIENCES: { value: string; label: string; sub: string }[] = [
  { value: 'all',       label: 'Everyone',          sub: 'Passenger app, driver app, tourists' },
  { value: 'passenger', label: 'Passenger app only', sub: 'Riders + tourists, not drivers' },
  { value: 'driver',    label: 'Driver app only',    sub: 'Drivers only, useful for fleet promos' },
  { value: 'tourist',   label: 'Tourists only',      sub: 'Visitors with Tourist mode enabled' },
];

// Weather conditions the ad resolver understands. The mobile apps poll
// Dominica's live weather every ~15 min and pass the resolved label into
// the resolver, so triggers picked here gate serving end-to-end. Mirrors
// the list in EditCampaignDialog so create + edit stay in lock-step.
const WEATHER_OPTIONS = [
  'rain', 'heavy_rain', 'thunderstorm', 'cloudy', 'clear', 'hot', 'cold',
];

const FESTIVAL_PRESETS = [
  { slug: 'world_creole_music_festival', label: 'World Creole Music Festival', loc: 'Roseau' },
  { slug: 'carnival', label: 'Carnival (Mas Domnik)', loc: 'Roseau' },
  { slug: 'independence_festival', label: 'Independence Festival', loc: 'Nationwide' },
  { slug: 'jazz_n_creole', label: 'Jazz \'n Creole', loc: 'Cabrits' },
];

interface TargetingStepProps {
  mode: WizardMode;
}

export default function TargetingStep({ mode }: TargetingStepProps) {
  const store = useWizardStore(mode);
  const {
    hourStart,
    hourEnd,
    corridorIds,
    festivalMode,
    eventSlug,
    eventLocationLabel,
    audience,
    campaignType,
    weatherTriggers,
    targetRadiusKm,
    campaignTypes,
  } = store;

  const toggleWeather = (w: string) => {
    const next = weatherTriggers.includes(w)
      ? weatherTriggers.filter((x) => x !== w)
      : [...weatherTriggers, w];
    store.set({ weatherTriggers: next });
  };

  const typeDef = campaignType ? getCampaignTypeByKey(campaignType) : undefined;
  const selectedPlacements = (campaignTypes.length > 0 ? campaignTypes : campaignType ? [campaignType] : [])
    .map((k) => getCampaignTypeByKey(k)?.placement)
    .filter((p): p is string => Boolean(p));
  const radiusApplies = selectedPlacements.some((p) => p.startsWith('passenger_sheet_'));

  // ── Corridor list — fetched live from Supabase ─────────────────────
  const [corridors, setCorridors] = useState<CorridorRow[]>([]);
  const [corridorsLoading, setCorridorsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('corridors')
          .select('id, name')
          .eq('is_active', true)
          .eq('is_draft', false)
          .order('name');
        if (cancelled) return;
        if (error) throw error;
        setCorridors((data as CorridorRow[]) || []);
      } catch (err) {
        console.error('Failed to load corridors:', err);
        // Fallback list so the wizard isn't completely broken if RLS blocks
        setCorridors([
          { id: 'west-coast', name: 'West Coast (Roseau ↔ Portsmouth)' },
          { id: 'east-coast', name: 'East Coast (Roseau ↔ Marigot)' },
          { id: 'south', name: 'South (Roseau ↔ Grand Bay)' },
          { id: 'north-east', name: 'North East (Portsmouth ↔ Calibishie)' },
        ]);
      } finally {
        if (!cancelled) setCorridorsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleCorridor = (id: string) => {
    const next = corridorIds.includes(id)
      ? corridorIds.filter((x) => x !== id)
      : [...corridorIds, id];
    store.set({ corridorIds: next });
  };

  // ── Festival mode interactions ──────────────────────────────────────
  const onFestivalToggle = (next: boolean) => {
    if (next) {
      store.set({
        festivalMode: true,
        templatePlacement: 'festival_event_takeover',
      });
    } else {
      // Restore the original placement from the campaign type
      store.set({
        festivalMode: false,
        templatePlacement: typeDef?.placement || '',
        eventSlug: '',
        eventLocationLabel: '',
      });
    }
  };

  const fmtHour = (h: number) =>
    `${String(h).padStart(2, '0')}:00${h === 24 ? '' : ''}`;

  const isAllDay = hourStart === 0 && hourEnd === 24;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-white">Targeting</h2>

      {/* Time-of-day window — dual-thumb slider */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" /> Time-of-day window
          </Label>
          <span
            className={`text-sm font-mono ${
              isAllDay ? 'text-gray-500' : 'text-emerald-400'
            }`}
          >
            {isAllDay ? 'All day' : `${fmtHour(hourStart)} → ${fmtHour(hourEnd)}`}
          </span>
        </div>
        <RadixSlider.Root
          className="relative flex items-center select-none touch-none w-full h-6"
          min={0}
          max={24}
          step={1}
          minStepsBetweenThumbs={1}
          value={[hourStart, hourEnd]}
          onValueChange={(v) => {
            const [a, b] = v;
            store.set({ hourStart: a, hourEnd: b });
          }}
        >
          <RadixSlider.Track className="bg-gray-800 relative grow rounded-full h-1.5">
            <RadixSlider.Range className="absolute bg-gradient-to-r from-cyan-400 to-emerald-400 rounded-full h-full" />
          </RadixSlider.Track>
          <RadixSlider.Thumb
            className="block w-5 h-5 bg-emerald-400 rounded-full shadow-lg shadow-emerald-500/30 hover:bg-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 focus:ring-offset-gray-900"
            aria-label="Start hour"
          />
          <RadixSlider.Thumb
            className="block w-5 h-5 bg-emerald-400 rounded-full shadow-lg shadow-emerald-500/30 hover:bg-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 focus:ring-offset-gray-900"
            aria-label="End hour"
          />
        </RadixSlider.Root>
        <div className="flex items-center justify-between text-[10px] text-gray-500">
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>24:00</span>
        </div>
        {typeDef && hourStart !== typeDef.defaultHourStart || (typeDef && hourEnd !== typeDef.defaultHourEnd) ? (
          <p className="text-[11px] text-amber-400/80">
            You've changed the default {typeDef?.label} window
            ({fmtHour(typeDef!.defaultHourStart)}–{fmtHour(typeDef!.defaultHourEnd)}).
            That's fine. Just confirm this is what the client agreed to.
          </p>
        ) : null}
      </div>

      {/* Audience */}
      <div className="space-y-2 border-t border-gray-800 pt-5">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 font-medium flex items-center gap-2">
            <Users className="w-4 h-4" /> Show this ad to
          </Label>
          <span className="text-[11px] text-gray-500">Pick one. “Everyone” is the most common.</span>
        </div>

        {/* Sponsored map pins are placed on the map itself, which both
            passenger and driver apps render. The audience filter only
            applies to in-app banners / takeovers, so be explicit here so
            the operator never thinks they need to "duplicate" the campaign. */}
        {typeDef?.placement === 'map_pin_featured' && (
          <div className="flex items-start gap-2 text-[11px] text-cyan-200 bg-cyan-500/10 border border-cyan-500/30 rounded-md p-2.5">
            <Globe className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold text-cyan-100">Sponsored Map Pin:</span>{' '}
              this pin is automatically shown on the passenger map AND the driver
              map regardless of the audience selection below. Audience only
              affects in-app banner placements.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {AUDIENCES.map((a) => {
            const selected = audience === a.value;
            return (
              <button
                key={a.value}
                type="button"
                onClick={() => store.set({ audience: a.value })}
                className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                  selected
                    ? 'bg-emerald-500/10 border-emerald-500/40 ring-1 ring-emerald-500/20'
                    : 'bg-gray-800/30 border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2">
                  {selected && <span className="text-emerald-400 text-xs">✓</span>}
                  <span
                    className={`text-sm font-semibold ${
                      selected ? 'text-emerald-300' : 'text-white'
                    }`}
                  >
                    {a.label}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">{a.sub}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Corridors */}
      <div className="space-y-2 border-t border-gray-800 pt-5">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 font-medium flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Master corridors
          </Label>
          {corridorIds.length > 0 && (
            <button
              type="button"
              className="text-[11px] text-gray-500 hover:text-gray-300"
              onClick={() => store.set({ corridorIds: [] })}
            >
              Clear ({corridorIds.length})
            </button>
          )}
        </div>
        <p className="text-[11px] text-gray-500">
          Leave empty to run on every corridor. Pick specific corridors to focus
          a campaign (e.g. only show the Fort Young banner to riders on the
          west coast).
        </p>
        {corridorsLoading ? (
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading corridors…
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {corridors.map((c) => {
              const active = corridorIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCorridor(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    active
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                      : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {active && <span className="mr-1">✓</span>}
                  {c.name}
                </button>
              );
            })}
            <Badge
              variant="outline"
              className={`text-[10px] ${
                corridorIds.length === 0
                  ? 'border-emerald-500/30 text-emerald-400'
                  : 'border-gray-700 text-gray-500'
              }`}
            >
              <Globe className="w-2.5 h-2.5 mr-1" />
              {corridorIds.length === 0 ? 'All corridors' : `${corridorIds.length} selected`}
            </Badge>
          </div>
        )}
      </div>

      {/* Proximity radius */}
      <div className="space-y-2 border-t border-gray-800 pt-5">
        <Label className="text-gray-300 font-medium flex items-center gap-2">
          <Crosshair className="w-4 h-4" /> Proximity radius
        </Label>
        <p className="text-[11px] text-gray-500">
          For location-based banners (a restaurant, hotel, shop), only show
          this ad to riders within this many km of the business. On the idle
          screen it uses the rider's location, and on the confirm/active
          screens it uses their destination. Leave blank to use the default
          (8&nbsp;km). Ignored for island-wide brand ads with no location.
        </p>
        {selectedPlacements.length > 0 && !radiusApplies && (
          <p className="text-[11px] text-amber-300">
            The placements you picked show island-wide, so this setting has no
            effect. It only applies to the bottom sheet carousels.
          </p>
        )}
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={50}
            step={1}
            inputMode="numeric"
            value={targetRadiusKm}
            onChange={(e) => store.set({ targetRadiusKm: e.target.value })}
            className="bg-gray-900 border-gray-700 text-white text-sm w-28"
            placeholder="8"
          />
          <span className="text-xs text-gray-500">km</span>
          {targetRadiusKm ? (
            <button
              type="button"
              className="text-[11px] text-gray-500 hover:text-gray-300 ml-1"
              onClick={() => store.set({ targetRadiusKm: '' })}
            >
              Use default
            </button>
          ) : (
            <Badge variant="outline" className="text-[10px] border-gray-700 text-gray-500">
              Default 8 km
            </Badge>
          )}
        </div>
      </div>

      {/* Weather triggers */}
      <div className="space-y-2 border-t border-gray-800 pt-5">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 font-medium flex items-center gap-2">
            <CloudRain className="w-4 h-4" /> Weather triggers
          </Label>
          <span className="text-[11px] text-gray-500">
            {weatherTriggers.length === 0 ? 'Any weather' : `${weatherTriggers.length} selected`}
          </span>
        </div>
        <p className="text-[11px] text-gray-500">
          Only serve this ad in certain conditions, e.g. an umbrella or café
          promo on rainy days. The app reads Dominica's live weather and the
          resolver matches on exact labels, so pick several for broad coverage
          (rain + heavy&nbsp;rain + thunderstorm). Leave empty for any weather.
        </p>
        <div className="flex flex-wrap gap-2">
          {WEATHER_OPTIONS.map((w) => {
            const active = weatherTriggers.includes(w);
            return (
              <button
                key={w}
                type="button"
                onClick={() => toggleWeather(w)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                  active
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
                }`}
              >
                {active && <span className="mr-1">✓</span>}
                {w.replace('_', ' ')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Festival mode */}
      <div className="space-y-3 border-t border-gray-800 pt-5">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 font-medium flex items-center gap-2">
            <PartyPopper className="w-4 h-4 text-amber-400" /> Festival mode
          </Label>
          <Switch
            checked={festivalMode}
            onCheckedChange={onFestivalToggle}
            disabled={mode === 'festival' /* always on in festival tab */}
          />
        </div>
        <p className="text-[11px] text-gray-500">
          Tag this ad as a festival/event takeover. It will run only during
          the festival's published window and override the normal commute
          ads on the home screen.
        </p>

        {festivalMode && (
          <div className="space-y-3 pl-3 border-l-2 border-amber-500/30">
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Event preset</Label>
              <div className="flex flex-wrap gap-2">
                {FESTIVAL_PRESETS.map((fp) => (
                  <button
                    key={fp.slug}
                    type="button"
                    onClick={() =>
                      store.set({
                        eventSlug: fp.slug,
                        eventLocationLabel: fp.loc,
                      })
                    }
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      eventSlug === fp.slug
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {fp.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-gray-500 text-xs">Event slug</Label>
                <Input
                  value={eventSlug}
                  onChange={(e) => store.set({ eventSlug: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white text-sm font-mono"
                  placeholder="carnival_2026"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-500 text-xs">Location label</Label>
                <Input
                  value={eventLocationLabel}
                  onChange={(e) => store.set({ eventLocationLabel: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white text-sm"
                  placeholder="Roseau"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
